import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Row} from "reactstrap";
import Paper from "@mui/material/Paper";
import PrintCatalogueViewStepperSection from "./sections/stepper";
import PrintCatalogueViewBottomActionBar from "./sections/bottom-action-bar";
import Api from "../../../../../../core/services/api/api";
import BizPrintService from "../../../../../../core/services/biz-print/biz-print-service";
import BizPrintTemplatesDialog from "../../../../../components/dialogs/biz-print-templates";
import PrintCatalogueViewProductSelectionSection from "./sections/product-selection";
import PrintCatalogueViewConfigureGroupsSection from "./sections/configure-groups";
import PrintCatalogueViewReviewSection from "./sections/review";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import Utils, {stringComparator} from "../../../../../../core/services/utils/utils";
import ToastService from "../../../../../../core/services/toast";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const initialBizPrintDialogState = {
    open: false,
    pageName: null,
    pageData: null,
}

export const printCatalogueProductSelectionInitialState = {
    selectedCategories: [],
    selectedProducts: [],
    selectedTags: [],
    displayTags: [],
}

export const printCatalogueConfigureGroupsInitialState = {
    categories: [],
    makes: [],
    tags: [],
    products: [],
}

export const printCatalogueReviewInitialState = {
    categories: [],
    makes: [],
    tags: [],
    products: [],
}

/**
 * Different steps for printing a catalogue.
 */
export const PrintCatalogueViewSteps = {
    productSelection: 'productSelection',
    configureGroups: 'configureGroups',
    review: 'review',
}

const PrintCatalogueView = () => {
    const [productSelectionState, setProductSelectionState] = useState(printCatalogueProductSelectionInitialState);
    const [configureGroupsState, setConfigureGroupsState] = useState(printCatalogueConfigureGroupsInitialState);
    const [reviewState, setReviewState] = useState(printCatalogueReviewInitialState);
    const [step, _setStep] = useState(PrintCatalogueViewSteps.productSelection);
    const [updatingStep, setUpdatingStep] = useState(false);
    const [bizPrintDialogState, setBizPrintDialogState] = useState(initialBizPrintDialogState);
    const isMounted = useIsMounted();

    const {configureNotebookTags} = useConfigureNotebook('print-catalogue-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.printCatalogue);
        configureBehavioralTool((tags) => tags.printCatalogue);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Merges the previous list with the new list while keeping the order of the previous list.
     * @param prevList  The previous list
     * @param newList   The new list
     * @return {*[]}
     */
    const mergeListsKeepingOrder = useCallback((prevList, newList) => {
        const mergedList = [...prevList];

        newList.forEach(newEntry => {
            const index = mergedList.findIndex(mergedEntry => mergedEntry.id === newEntry.id);

            if (index === -1) {
                mergedList.push(newEntry); // Add new category to the end of the merged list
            } else {
                mergedList[index] = {...prevList[index], ...newEntry}; // Merge existing category with new category
            }
        });

        // Remove categories that are in the previous list but not in the new list
        mergedList.filter(mergedEntry => newList.some(newEntry => newEntry.id === mergedEntry.id));

        return mergedList;
    }, []);

    /**
     * Submits the product selection and updates the configure groups state.
     *
     * @type {(function(): Promise<boolean>)}
     */
    const submitProductSelection = useCallback(async () => {
        const response = await Api.getCatalogueProducts({
            partIds: productSelectionState.selectedProducts.map(e => e.id),
            categoryIds: productSelectionState.selectedCategories.map(e => e.id),
            tagIds: productSelectionState.selectedTags.map(e => e.id),
            displayTagIds: productSelectionState.displayTags.map(e => e.id),
        });
        if (!isMounted())
            return false;
        if (!response.resultFlag)
            return false;
        if (!response.data?.length) {
            ToastService.warning({title: "No Products", message: "There are no products available for the selected filters."});
            return false;
        }
        const categories = response.data
            .map(e => ({
                id: e.parentCategoryId ?? -1,
                title: e.parentCategoryTitle ?? 'No Category Specified',
            }))
            .reduce((acc, curr) => {
                if (!acc.find(e => e.id === curr.id))
                    acc.push(curr);
                return acc;
            }, []);
        const makes = response.data
            .map(e => ({
                id: e.makeId ?? -1,
                title: e.makeTitle ?? 'No Make Specified',
            }))
            .reduce((acc, curr) => {
                if (!acc.find(e => e.id === curr.id))
                    acc.push(curr);
                return acc;
            }, []);

        setConfigureGroupsState(prevState => {
            // TODO: category --> fix this
            const newCategories = mergeListsKeepingOrder(prevState.categories, categories)
            // TODO: application --> fix this
            const newMakes = mergeListsKeepingOrder(prevState.makes, makes)

            return ({
                ...prevState,
                categories: newCategories,
                makes: newMakes,
                tags: productSelectionState.displayTags,
                products: response.data.map(e => {
                    // TODO: technical properties --> fix this
                    const properties = e.technicalProperties ?? [];
                    const groupedProperties = {};
                    for (const propertyValue of properties) {
                        groupedProperties[propertyValue.propertyId] = {
                            ...groupedProperties[propertyValue.propertyId],
                            title: propertyValue.propertyTitle,
                            id: propertyValue.propertyId,
                            value: (groupedProperties[propertyValue.propertyId]?.value ? `${groupedProperties[propertyValue.propertyId]?.value},` : "").concat(`${Utils.formatTechnicalPropertyValueDisplay(propertyValue.industryPropertyTypeId, propertyValue.value)}`),
                        };
                    }
                    const technicalProperties = Object.values(groupedProperties).filter(e => !!e.value);
                    technicalProperties.sort((a, b) => stringComparator(a.title, b.title));
                    return ({
                        ...e,
                        technicalProperties: technicalProperties,
                        coverImageFileName: e.coverImageFileName ? response?.configuration?.PartImageURL.concat(e.coverImageFileName ?? '') : '',
                        parentCategoryId: e.parentCategoryId ?? -1,
                        parentCategoryTitle: e.parentCategoryTitle?.concat(e.industryTitle ? ` (${e.industryTitle})` : ''),
                        makeId: e.makeId ?? -1,
                    })
                }),
            })
        });
        return true;
    }, [isMounted, mergeListsKeepingOrder, productSelectionState.displayTags, productSelectionState.selectedCategories, productSelectionState.selectedProducts, productSelectionState.selectedTags]);

    /**
     * Submits the group configuration and updates the review state.
     *
     * @type {(function(): void)}
     */
    const submitGroupConfiguration = useCallback(() => {
        const categories = [...configureGroupsState.categories];
        const makes = [...configureGroupsState.makes];
        setReviewState({
            products: configureGroupsState.products
                .map(e => ({
                    ...e,
                    category: categories.find(c => c.id === e.parentCategoryId),
                    make: configureGroupsState.makes.find(m => m.id === e.makeId),
                    tags: configureGroupsState.tags.filter((tag) => e.tags.findIndex(t => t.id === tag.id) !== -1),
                }))
                .filter(e => e.category)
                .sort((a, b) => {
                    const categoryAIndex = categories.findIndex(category => category.id === a.parentCategoryId);
                    const categoryBIndex = categories.findIndex(category => category.id === b.parentCategoryId);

                    // Compare the category IDs
                    const categoryComparison = categoryAIndex - categoryBIndex;

                    // If the category IDs are the same, compare the make IDs
                    if (categoryComparison === 0) {
                        const makeAIndex = makes.findIndex(make => make.id === a.makeId);
                        const makeBIndex = makes.findIndex(make => make.id === b.makeId);
                        return makeAIndex - makeBIndex;
                    }

                    return categoryComparison;

                }),
            categories: configureGroupsState.categories,
            makes: configureGroupsState.makes,
            tags: configureGroupsState.tags,
        })
    }, [configureGroupsState.categories, configureGroupsState.makes, configureGroupsState.products, configureGroupsState.tags]);

    /**
     * Submits the review and updates the print state.
     *
     * @type {(function(): void)}
     */
    const openPrintDialog = useCallback(() => {
        const catalogue = reviewState.products ?? [];
        setBizPrintDialogState({
            open: true,
            pageName: BizPrintService.pages.productsCatalogue.name,
            pageData: catalogue,
        })
    }, [reviewState.products]);

    /**
     * Sets the step of the view.
     *
     * 1. If the step is 'configureGroups' it will submit the product selection and if it is successful it will set the step.
     * 2. If the step is 'review' it will submit the group configuration and set the step.
     * 3. If the step is 'print' it will open the print dialog.
     * @type {(function(*): Promise<void>)}
     */
    const setStep = useCallback(async (step) => {
        setUpdatingStep(true);
        let success = true;
        switch (step) {
            case PrintCatalogueViewSteps.configureGroups:
                if (!await submitProductSelection())
                    success = false;
                break;
            case PrintCatalogueViewSteps.review:
                submitGroupConfiguration();
                break;
            case 'print':
                openPrintDialog();
                step = PrintCatalogueViewSteps.review;
                break;
            default:
                break;
        }
        setUpdatingStep(false);
        if (success)
            _setStep(step);
    }, [openPrintDialog, submitGroupConfiguration, submitProductSelection])

    /**
     * Resets the next states of the view.
     * @type {(function(): void)|*}
     */
    const onProductSelectionStateChanges = useCallback(() => {
        setConfigureGroupsState(printCatalogueConfigureGroupsInitialState);
        setReviewState(printCatalogueReviewInitialState);
    }, []);


    /**@type {ReactNode}*/
    const content = useMemo(() => {
        switch (step) {
            case PrintCatalogueViewSteps.productSelection:
                return (
                    <PrintCatalogueViewProductSelectionSection
                        productSelectionState={productSelectionState}
                        setProductSelectionState={setProductSelectionState}
                        resetNextStates={onProductSelectionStateChanges}
                        updatingStep={updatingStep}
                    />
                );
            case PrintCatalogueViewSteps.configureGroups:
                return (
                    <PrintCatalogueViewConfigureGroupsSection
                        configureGroupsState={configureGroupsState}
                        setConfigureGroupsState={setConfigureGroupsState}
                        updatingStep={updatingStep}
                    />
                );
            case PrintCatalogueViewSteps.review:
                return (
                    <PrintCatalogueViewReviewSection
                        reviewState={reviewState}
                        updatingStep={updatingStep}
                    />
                );
            default:
                return null;
        }
    }, [configureGroupsState, onProductSelectionStateChanges, productSelectionState, reviewState, step, updatingStep]);

    return (
        <>
            <div className={'print-catalogue-view'}
                // ref={mainSectionRef}
            >
                <Paper
                    className={'paper d-flex flex-column justify-content-center align-items-center'}
                >
                    <Row className='no-gutters w-100 d-flex justify-content-between align-items-start my-4 mt-3'>
                        <h2 className={'view-title m-0 ml-3'}>
                            Print Catalogue
                        </h2>
                    </Row>
                    <PrintCatalogueViewStepperSection
                        step={step}
                        setStep={setStep}
                        productSelectionState={productSelectionState}
                        configureGroupsState={configureGroupsState}
                        reviewState={reviewState}
                        updatingStep={updatingStep}
                    />
                    {content}
                </Paper>
                <PrintCatalogueViewBottomActionBar
                    // ref={bottomActionBarRef}
                    productSelectionState={productSelectionState}
                    configureGroupsState={configureGroupsState}
                    reviewState={reviewState}
                    step={step}
                    setStep={setStep}
                    updatingStep={updatingStep}
                />
            </div>
            <BizPrintTemplatesDialog
                open={bizPrintDialogState.open}
                close={() => setBizPrintDialogState(initialBizPrintDialogState)}
                pageName={bizPrintDialogState.pageName}
                pageData={bizPrintDialogState.pageData}
            />
        </>
    )
}

export default PrintCatalogueView;
